import {useLocation, Router, Routes, Route, useNavigate} from '@startlibs/components';
import { Slot, useToggle} from '@startlibs/core'
import React, {Suspense, useEffect, useRef, useState} from 'react'
import {LogoutButton, Navbar} from '../components/Navbar';
import {PatientApproval} from './PatientApproval'
import {PatientApprovalSuccess} from './status/PatientApprovalSuccess'
import {PatientCheckout} from './PatientCheckout'
import {PatientLogin} from './PatientLogin'
import {PatientOverview} from './PatientOverview'
import {PatientRecords} from './PatientRecords'
import {PatientReport} from './PatientReport'
import {lazyProviderInfo} from '../components/WithProvider'
import {PatientContactDetails} from './PatientContactDetails'
import {getFirstPage} from './utils/patientUtils'
import {PageContent, PageWrapper} from '../admin/steps/RequestSidebar'
import {PatientRequestSidebar} from './PatientRequestSidebar'
import {PatientMedicalRelease} from './PatientMedicalRelease'
import {PatientStatus} from './PatientStatus'
import {jwtGetFetcher, jwtPostFetcher} from '../utils/authFetch'
import {getJwt, setJwt} from '../hooks/useJwt'
import {confirmExitPage} from '../hooks/useConfirmExitPage'
import {PatientPayments} from './PatientPayments'
import {PatientAgreements} from './PatientSignTerms'
import {ProviderTitle} from '../components/ProviderTitle'
import {PatientCaseAwareness} from './PatientCaseAwereness'
import {willUseSuspense, WithLazyResource} from '../hooks/useSuspense'
import {LoadingPage} from '../components/PageLoading'
import {getTimezoneOffset} from '../utils/utils'
import {isReadOnlyShare} from './utils/caseRequestUtils'
import {ReadOnlyOverview} from './ReadOnlyOverview'
import {ReadonlyRequestSidebar} from './readonly/ReadonlyRequestSidebar'
import {Redirect} from "../components/Redirect";
import { FeedbackButton } from '../expert/ExpertRoutes';
import { CASE_CLOSED } from '../enums/CaseState';
import { isStateBefore } from '../request/utils';
import { RequestOverviewMedicalRecordsOnly } from '../request/RequestOverviewMedicalRecordsOnly';
import { PATIENT } from '../enums/UserRoles';
import { PatientContactInstitution } from './PatientContactInstitution';
import { PatientCreateRequestNew } from './PatientCreateRequestNew';
import {FormattedMessage} from "react-intl";
import _ from "lodash/fp";
import {ThemeProvider} from "styled-components";

const Identity = () => null

export const PatientRoutes = ({isNurse,isRefPhysician}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [caseRequest, setCaseRequest] = useState()
  const feedbackPopup = useToggle()
  const isCurrentStateBefore = isStateBefore(caseRequest && caseRequest.state)

  const urlType = (!isNurse && !isRefPhysician) ? 'patient' : isNurse ? 'nurse' : 'refphysician'

  if (location.pathname.indexOf('/access-case')>=0) {
    return <WithLazyResource value={lazyProviderInfo}>{providerInfo =>
      <PatientCaseAwareness providerInfo={providerInfo} setCaseRequest={setCaseRequest} isNurse={isNurse} isRefPhysician={isRefPhysician}/>
    }</WithLazyResource>
  }

  return <WithLazyResource value={lazyProviderInfo}>{providerInfo =>
    <>
      <ProviderTitle/>
      
        <Routes>
          <Route path={`access`} element={<Identity/>}/>
          {!caseRequest && <Route path={`${urlType}/`} element={<Identity/>}/>}
          <Route path={`${urlType}/records`} element={<Navbar isPatient menu={<Slot className="navbar-action" name="Navbar-Action"/>}/>}/>
          <Route path={`${urlType}/start`} element={<Navbar isPatient menu={<Slot className="navbar-action" name="Navbar-Action"/>}/>}/>
          <Route path="*" element={<Navbar
            withLocale
            isPatient
            subtitle={isReadOnlyShare(caseRequest) && "Shared access"}
            menu={<>
              {!isCurrentStateBefore(CASE_CLOSED) && caseRequest.requestId && <FeedbackButton icon="feedback" textOnly onClick={()=>{feedbackPopup.open()}} css="margin-right:2rem;"><FormattedMessage
                defaultMessage="Share feedback"
                description="Patient navbar, share feedback button"
              /></FeedbackButton>}
              <LogoutButton className="navbar-action" 
                onClick={() => confirmExitPage().then(() => jwtPostFetcher(getJwt())("/api/logout",""))
                  .finally(() => {
                    navigate(`${urlType}/access`)
                    setCaseRequest(null)
                  })}
              >
                <FormattedMessage
                  defaultMessage="Exit"
                  description="Patient navbar, exit button"
              /></LogoutButton>
            </>}/>
          }/>
        </Routes>
    
      {
        caseRequest && location.pathname.indexOf('/access') !== 0
          ? <PatientCase
            caseRequest={caseRequest}
            setCaseRequest={setCaseRequest}
            providerInfo={providerInfo}
            isRefPhysician={isRefPhysician} 
            feedbackPopup={feedbackPopup}
          />
          : <Router>
              <Routes>
                <Route path={`access`} element={<PatientLogin
                  isNurse={isNurse}
                  isRefPhysician={isRefPhysician}
                  providerInfo={providerInfo}
                  setCaseRequest={setCaseRequest}
                />}/>
                <Route path={`${urlType}/open`} element={<SuspenseStep Component={PatientLoad} setCaseRequest={setCaseRequest} providerInfo={providerInfo} isRefPhysician={isRefPhysician}/>}/>
                {providerInfo?.patientCaseCreation && <Route path={`${urlType}/start`} element={<PatientCreateCaseFromStart isNurse={isNurse} isRefPhysician={isRefPhysician} setCaseRequest={setCaseRequest} providerInfo={providerInfo}/>}/>}
                <Route path="*" element={<Redirect noThrow to={`/access`}/>}/>
              </Routes>
            </Router>
      }
    </>
  }</WithLazyResource>
}

const PatientCreateCaseFromStart = ({setCaseRequest,isNurse,isRefPhysician, providerInfo}) => {
  const categories = useToggle([])
  const markedAsPriority = useToggle()
  const formRef = useRef()

  return <PageWrapper responsiveWorkaround noSidebar={!isNurse && !isRefPhysician}>
    <PageContent>
      <SuspenseStep Component={PatientCreateRequestNew}
                    formRef={(isNurse || isRefPhysician) ? formRef : undefined}
                    onChange={(isNurse || isRefPhysician) ? (prev, next) => {
                      if (prev.markAsPriority !== next.markAsPriority) {
                        markedAsPriority.openWith(next.markAsPriority)
                      }
                      if (prev.categories !== next.categories) {
                        categories.openWith(next.categories)
                      }
                    } : undefined}
                    path="start" isNurse={isNurse} isRefPhysician={isRefPhysician} setCaseRequest={setCaseRequest} providerInfo={providerInfo} 
                    markedAsPriority={markedAsPriority} />
    </PageContent>
  </PageWrapper>
}
const StableThemeProvider = ThemeProvider

const SuspenseStep = ({Component, ...props}) => <Suspense fallback={<LoadingPage />}><Component {...props} /></Suspense>

const PatientCase = ({caseRequest, setCaseRequest, providerInfo, isNurse, isRefPhysician, feedbackPopup}) => {

  const urlType = (!isNurse && !isRefPhysician) ? 'patient' : isNurse ? 'nurse' : 'refphysician'

  const readOnlyShare = isReadOnlyShare(caseRequest)
  const showReadOnlyMedicalRecords = (readOnlyShare && caseRequest?.permission?.medicalRecords)

  const canAccessCaseOverview = providerInfo?.patientAccessFunctions?.caseOverview && !readOnlyShare


  return <StableThemeProvider theme={_.set('mobileSidebar',true)}>
    <PageWrapper responsiveWorkaround>
    {
      readOnlyShare
        ? <ReadonlyRequestSidebar providerInfo={providerInfo} caseRequest={caseRequest}/>
        : <PatientRequestSidebar providerInfo={providerInfo} caseRequest={caseRequest} isRefPhysician={isRefPhysician}/>
    }
    <PageContent>
      <Routes>
        <Route path={`${urlType}/records`} element={<PatientRecords caseRequest={caseRequest} setCaseRequest={setCaseRequest} isRefPhysician={isRefPhysician} feedbackPopup={feedbackPopup}/>}/>

        <Route path={`${urlType}/release`} element={<PatientMedicalRelease caseRequest={caseRequest} setCaseRequest={setCaseRequest} feedbackPopup={feedbackPopup}/>}/>
        <Route path={`${urlType}/agreements`} element={<SuspenseStep Component={PatientAgreements} caseRequest={caseRequest} setCaseRequest={setCaseRequest} feedbackPopup={feedbackPopup} isRefPhysician={isRefPhysician} />}/>

        <Route path={`${urlType}/approval`} element={<PatientApproval caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
        <Route path={`${urlType}/checkout/:position`} element={<PatientCheckout providerInfo={providerInfo} caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
        <Route path={`${urlType}/payments`} element={<PatientPayments caseRequest={caseRequest} setCaseRequest={setCaseRequest} feedbackPopup={feedbackPopup} isRefPhysician={isRefPhysician}/>}/>

        <Route path={`${urlType}/details`} element={<PatientContactDetails caseRequest={caseRequest} setCaseRequest={setCaseRequest} feedbackPopup={feedbackPopup}/>}/>

        <Route path={`${urlType}/report`} element={<PatientReport caseRequest={caseRequest} feedbackPopup={feedbackPopup}/>}/>
        <Route path={`${urlType}/paid`} element={<PatientApprovalSuccess caseRequest={caseRequest}/>}/>
        <Route path={`${urlType}/status`} element={<PatientStatus caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
        <Route path={`${urlType}/contact-institution`} element={<PatientContactInstitution caseRequest={caseRequest} setCaseRequest={setCaseRequest} feedbackPopup={feedbackPopup}/> }/>

        {
          readOnlyShare
          ? <Route path={`${urlType}/overview`} element={<ReadOnlyOverview caseRequest={caseRequest} setCaseRequest={setCaseRequest} allowUpload={false} allowDownload={false} showHiddenRecords={false} />}/>
          : canAccessCaseOverview && <Route path={`${urlType}/overview`} element={<PatientOverview caseRequest={caseRequest} setCaseRequest={setCaseRequest} isRefPhysician={isRefPhysician}/>}/>
        }
        {
          showReadOnlyMedicalRecords &&
            <Route path={`${urlType}/medical-records`} element={
              <RequestOverviewMedicalRecordsOnly 
                caseRequest={caseRequest} 
                setCaseRequest={setCaseRequest} 
                allowUpload={false} 
                allowDownload={false} 
                role={PATIENT}
                allowDownloadMedicalImages={caseRequest?.permission?.allowDownload}
                isReadOnlyShare={true}
                showHiddenRecords={false} 
              />
            }/>
        }
        <Route path={`${urlType}/login`} element={<PatientLogin
          isNurse={isNurse}
          isRefPhysician={isRefPhysician}
          providerInfo={providerInfo}
          setCaseRequest={setCaseRequest}
        />}/>
        <Route path={`${urlType}/start`} element={<SuspenseStep Component={PatientCreateRequestNew} setCaseRequest={setCaseRequest} providerInfo={providerInfo}/>}/>
        <Route element={<Redirect noThrow to={caseRequest ? getFirstPage(caseRequest, providerInfo, isRefPhysician) : '/patient/'} from="*"/>}/>
      </Routes>
    </PageContent>
  </PageWrapper>
  </StableThemeProvider>
}


// const useLoadCase = willUseSuspense((jwt) => jwtGetFetcher(jwt)(`/api/viewCaseRequest?timezoneOffset=${getTimezoneOffset()}`))
const useLoadCase = willUseSuspense((jwt) => jwtGetFetcher(jwt)(`/api/patient/viewCaseRequest?timezoneOffset=${getTimezoneOffset()}`))

const PatientLoad = ({setCaseRequest, providerInfo, isRefPhysician}) => {
  const navigate = useNavigate()
  const loadedCaseRequest = useLoadCase(window.JWT_URL_PARAM)
  useEffect(() => {
    setJwt(window.JWT_URL_PARAM)
    setCaseRequest(loadedCaseRequest)
    navigate(getFirstPage(loadedCaseRequest, providerInfo, isRefPhysician))
  },[])
  return null
}
